import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QrCode, Package, Calendar, User, DollarSign } from "lucide-react";
import { PurchaseListResponse } from "@/types/purchaseList.types";

interface PurchaseListCardProps {
  purchaseList: PurchaseListResponse;
  onGenerateQR: () => void;
  className?: string;
}

export const PurchaseListCard: React.FC<PurchaseListCardProps> = ({
  purchaseList,
  onGenerateQR,
  className,
}) => {
  // Formatear fecha
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-CL", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Formatear precio
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Obtener estado del pago
  const getPaymentStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return { status: "Pendiente", variant: "secondary" as const };
      case "paid":
        return { status: "Pagado", variant: "default" as const };
      case "failed":
        return { status: "Fallido", variant: "destructive" as const };
      default:
        return { status: "Desconocido", variant: "outline" as const };
    }
  };

  // Obtener estado de la lista
  const getListStatus = (isActive: boolean, saldoRestante: number) => {
    if (!isActive) {
      return { status: "Inactiva", variant: "secondary" as const };
    }
    if (saldoRestante === 0) {
      return { status: "Agotada", variant: "destructive" as const };
    }
    if (saldoRestante < purchaseList.saldo_total_comprado * 0.3) {
      return { status: "Pocas unidades", variant: "outline" as const };
    }
    return { status: "Activa", variant: "default" as const };
  };

  const paymentStatus = getPaymentStatus(purchaseList.payment_status);
  const listStatus = getListStatus(purchaseList.is_active, purchaseList.saldo_restante);

  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-3 flex-1">
            {/* Header con título y estados */}
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h4 className="font-semibold text-lg">
                  {purchaseList.producto?.nombre || "Producto"}
                </h4>
                <p className="text-sm text-muted-foreground">
                  ID: {purchaseList.id.slice(0, 8)}...
                </p>
              </div>
              <div className="flex gap-2">
                <Badge variant={paymentStatus.variant}>
                  {paymentStatus.status}
                </Badge>
                <Badge variant={listStatus.variant}>
                  {listStatus.status}
                </Badge>
              </div>
            </div>

            {/* Información del producto */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Package className="h-4 w-4" />
                  <span>Producto</span>
                </div>
                <p className="font-medium">
                  {purchaseList.producto?.nombre || "N/A"}
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>Apoderado</span>
                </div>
                <p className="font-medium">
                  {purchaseList.apoderado ? 
                    `${purchaseList.apoderado.nombre} ${purchaseList.apoderado.apellido}` : 
                    "N/A"
                  }
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <DollarSign className="h-4 w-4" />
                  <span>Precio</span>
                </div>
                <p className="font-medium">
                  {purchaseList.producto?.precio ? 
                    formatPrice(purchaseList.producto.precio) : 
                    "N/A"
                  }
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Fecha</span>
                </div>
                <p className="font-medium">
                  {formatDate(purchaseList.fechaCompra)}
                </p>
              </div>
            </div>

            {/* Información de saldo */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Saldo Total:</span>
                <span className="font-medium">{purchaseList.saldo_total_comprado}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Saldo Restante:</span>
                <span className="font-medium">{purchaseList.saldo_restante}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Utilizado:</span>
                <span className="font-medium">
                  {purchaseList.saldo_total_comprado - purchaseList.saldo_restante}
                </span>
              </div>
            </div>

            {/* Barra de progreso */}
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Progreso de uso</span>
                <span>
                  {Math.round(
                    ((purchaseList.saldo_total_comprado - purchaseList.saldo_restante) /
                      purchaseList.saldo_total_comprado) *
                      100
                  )}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.round(
                      ((purchaseList.saldo_total_comprado - purchaseList.saldo_restante) /
                        purchaseList.saldo_total_comprado) *
                        100
                    )}%`,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Acciones */}
          <div className="flex flex-col gap-2 ml-4">
            {purchaseList.qr_code ? (
              <div className="text-center space-y-2">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center border border-gray-300 dark:border-gray-600">
                  <QrCode className="h-6 w-6 text-gray-500" />
                </div>
                <p className="text-xs text-muted-foreground font-mono">
                  {purchaseList.qr_code.slice(0, 8)}...
                </p>
                <Button size="sm" variant="outline" onClick={onGenerateQR}>
                  <QrCode className="h-4 w-4 mr-1" />
                  Regenerar
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={onGenerateQR}>
                <QrCode className="h-4 w-4 mr-1" />
                Generar QR
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 